import React, { useState, useEffect } from 'react';
import { getProfitability } from '../../api/finance';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://kanguroo-crm-production.up.railway.app/api/v1';
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((c) => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const fmt = (n, cur = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n ?? 0);

export default function Profitability() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('profit');

  useEffect(() => {
    Promise.all([getProfitability(), api.get('/customers/')])
      .then(([profRes, custRes]) => {
        setRows(profRes.data);
        const map = {};
        custRes.data.forEach((c) => { map[c.id] = c.company_name || c.full_name; });
        setCustomers(map);
      })
      .catch(() => alert('Veri yuklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const sorted = [...rows].sort((a, b) => b[sortBy] - a[sortBy]);
  const totals = rows.reduce((acc, r) => ({
    service_revenue: acc.service_revenue + r.service_revenue,
    total_expenses: acc.total_expenses + r.total_expenses,
    profit: acc.profit + r.profit,
  }), { service_revenue: 0, total_expenses: 0, profit: 0 });
  const maxProfit = Math.max(...rows.map((r) => Math.abs(r.profit)), 1);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Karlılık Raporu</h1>
          <p className="text-sm text-gray-500 mt-1">Musteri bazli gelir / gider / kar analizi</p>
        </div>
        <div className="flex gap-2">
          {[['profit', 'Kara gore'], ['service_revenue', 'Gelire gore'], ['margin_percent', 'Margin gore']].map(([val, label]) => (
            <button key={val} onClick={() => setSortBy(val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${sortBy === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <p className="text-sm text-blue-600 font-medium">Toplam Hizmet Geliri</p>
          <p className="text-2xl font-bold text-blue-800 mt-1">{fmt(totals.service_revenue)}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <p className="text-sm text-red-600 font-medium">Toplam Gider</p>
          <p className="text-2xl font-bold text-red-800 mt-1">{fmt(totals.total_expenses)}</p>
        </div>
        <div className={`border rounded-xl p-5 ${totals.profit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <p className={`text-sm font-medium ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>Net Kar</p>
          <p className={`text-2xl font-bold mt-1 ${totals.profit >= 0 ? 'text-green-800' : 'text-red-800'}`}>{fmt(totals.profit)}</p>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><p className="text-4xl mb-2">📊</p><p>Veri bulunamadi</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">Musteri</th>
                <th className="px-4 py-3 text-right">Hizmet Geliri</th>
                <th className="px-4 py-3 text-right">Gider</th>
                <th className="px-4 py-3 text-right">Net Kar</th>
                <th className="px-4 py-3 text-right">Margin</th>
                <th className="px-4 py-3 text-left pl-8">Gorsel</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((row) => {
                const isPositive = row.profit >= 0;
                const barWidth = Math.round((Math.abs(row.profit) / maxProfit) * 100);
                return (
                  <tr key={row.customer_id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-medium text-gray-800">{customers[row.customer_id] || row.customer_id?.slice(0, 8)}</td>
                    <td className="px-4 py-3 text-right text-blue-700 font-medium">{fmt(row.service_revenue)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{fmt(row.total_expenses)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${isPositive ? 'text-green-700' : 'text-red-700'}`}>{isPositive ? '+' : ''}{fmt(row.profit)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{row.margin_percent?.toFixed(1)}%</span>
                    </td>
                    <td className="px-4 py-3 pl-8">
                      <div className="w-32 bg-gray-100 rounded-full h-2">
                        <div className={`h-2 rounded-full ${isPositive ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${barWidth}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
